/**
 * O meteograma da ficha do ponto: faixas empilhadas sobre UM eixo de tempo
 * (a grade de 3 h), com um cursor que marca a mesma hora em todas e a marca
 * do instante do mapa — a moldura comum `ui/GraficoTempo`, como a ficha das
 * estações (`meteo/MeteoPopup`). O eixo é o dos dias e horas DO LUGAR.
 */
import { useMemo, type ReactNode } from 'react';
import { GRAFICO_A, GraficoTempo } from '../ui/GraficoTempo';
import { centroPct } from '../ui/fatias';
import { horaLocal, inicioDoDiaLocal, rotuloDia } from './ponto';

export interface Cursor {
  sob: number | null;
  aoSob: (i: number | null) => void;
}

/** Trechos contínuos (índices consecutivos com valor). */
function trechos(v: ArrayLike<number>): number[][] {
  const out: number[][] = [];
  let atual: number[] = [];
  for (let i = 0; i < v.length; i++) {
    if (Number.isFinite(v[i])) atual.push(i);
    else if (atual.length) {
      out.push(atual);
      atual = [];
    }
  }
  if (atual.length) out.push(atual);
  return out;
}

export interface Linha {
  serie: ArrayLike<number>;
  cor: string;
  /** Pontos em vez de linha (a rajada: um pico do intervalo, não uma média). */
  pontos?: boolean;
  tracejada?: boolean;
}

/** Uma faixa sombreada entre duas séries (a temperatura de 10% a 90% dos cenários do WeatherNext 2). */
export interface Banda {
  de: ArrayLike<number>;
  ate: ArrayLike<number>;
  cor: string;
}

/** Faixa de linhas na mesma escala; `fixo` = [mín, máx] da escala, senão a dos dados (e da banda) com `piso` de amplitude. */
export function Linhas(props: {
  linhas: Linha[];
  instantes: readonly number[];
  iAtual: number;
  rotulo: string;
  altura: number;
  cursor: Cursor;
  fixo?: [number, number];
  piso?: number;
  /** Zonas de fundo (as faixas da CONAMA na fumaça): de `de` até `ate`, na cor. */
  zonas?: { de: number; ate: number; cor: string }[];
  /** Atrás das linhas, entre duas séries; entra na escala. */
  banda?: Banda;
}) {
  const { linhas, instantes, banda } = props;
  const n = instantes.length;
  const escala = useMemo(() => {
    if (props.fixo) return { base: props.fixo[0], amp: props.fixo[1] - props.fixo[0] };
    const vs = [...linhas.map((l) => l.serie), ...(banda ? [banda.de, banda.ate] : [])].flatMap((s) => Array.from(s).filter(Number.isFinite));
    if (!vs.length) return null;
    let lo = Math.min(...vs);
    let hi = Math.max(...vs);
    const piso = props.piso ?? 1;
    if (hi - lo < piso) {
      const meio = (hi + lo) / 2;
      lo = meio - piso / 2;
      hi = meio + piso / 2;
    }
    return { base: lo, amp: hi - lo };
  }, [linhas, banda, props.fixo, props.piso]);
  const partes = useMemo(() => linhas.map((l) => trechos(l.serie)), [linhas]);
  // A banda só onde as duas bordas têm valor.
  const partesDaBanda = useMemo(
    () => (banda ? trechos(Float32Array.from({ length: Math.min(banda.de.length, banda.ate.length) }, (_, i) => (Number.isFinite(banda.de[i]) && Number.isFinite(banda.ate[i]) ? 0 : NaN))) : []),
    [banda],
  );
  // Sem valor: um aviso da MESMA altura do gráfico (a ficha não pula ao trocar de período).
  if (!escala)
    return (
      <div className="flex items-center text-[11px] text-ink-400" style={{ height: props.altura }}>
        Sem previsão neste período.
      </div>
    );
  const A = GRAFICO_A;
  const FOLGA = 2.5;
  const y = (v: number) => A - FOLGA - ((v - escala.base) / escala.amp) * (A - 2 * FOLGA);
  return (
    <GraficoTempo
      instantes={instantes}
      iAtual={props.iAtual}
      rotulo={props.rotulo}
      valor={() => null}
      resumo={null}
      soDesenho
      altura={props.altura}
      sob={props.cursor.sob}
      aoSob={props.cursor.aoSob}
      desenho={({ w, sob }) => (
        <>
          {props.zonas?.map((z) => {
            const topo = Math.min(z.ate, escala.base + escala.amp);
            if (topo <= Math.max(z.de, escala.base)) return null;
            const y0 = y(Math.max(z.de, escala.base));
            const y1 = y(topo);
            return <rect key={z.de} x="0" width={n * w} y={y1} height={Math.max(0, y0 - y1)} fill={z.cor} opacity="0.28" />;
          })}
          {banda &&
            partesDaBanda.map((t) => (
              <polygon
                key={`b${t[0]}`}
                points={[...t.map((i) => `${((i + 0.5) * w).toFixed(2)},${y(banda.ate[i]!).toFixed(2)}`), ...[...t].reverse().map((i) => `${((i + 0.5) * w).toFixed(2)},${y(banda.de[i]!).toFixed(2)}`)].join(' ')}
                fill={banda.cor}
                opacity="0.28"
                stroke="none"
              />
            ))}
          {sob !== null && <line x1={(sob + 0.5) * w} y1="0" x2={(sob + 0.5) * w} y2={A} stroke="rgba(255,255,255,0.35)" strokeWidth="1" vectorEffect="non-scaling-stroke" />}
          {linhas.map((l, k) =>
            l.pontos
              ? null
              : partes[k]!.map((t) => (
                  <polyline
                    key={`${k}-${t[0]}`}
                    points={t.map((i) => `${((i + 0.5) * w).toFixed(2)},${y(l.serie[i]!).toFixed(2)}`).join(' ')}
                    fill="none"
                    stroke={l.cor}
                    strokeWidth="1.5"
                    strokeDasharray={l.tracejada ? '3 2' : undefined}
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                )),
          )}
        </>
      )}
      sobre={({ destaque }) => (
        <>
          {linhas.map((l, k) =>
            l.pontos
              ? Array.from(l.serie).map((v, i) =>
                  Number.isFinite(v) ? (
                    <span
                      key={`p${k}-${i}`}
                      aria-hidden
                      className="pointer-events-none absolute size-[4px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                      style={{ left: `${centroPct(i, n)}%`, top: `${(y(v) / A) * 100}%`, background: l.cor }}
                    />
                  ) : null,
                )
              : null,
          )}
          {destaque !== null &&
            linhas.map((l, k) => {
              const v = l.serie[destaque];
              if (!Number.isFinite(v) || l.pontos || l.tracejada) return null;
              return (
                <span
                  key={`d${k}`}
                  aria-hidden
                  className="pointer-events-none absolute size-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-ink-900"
                  style={{ left: `${centroPct(destaque, n)}%`, top: `${(y(v!) / A) * 100}%`, background: l.cor }}
                />
              );
            })}
        </>
      )}
    />
  );
}

/** Barras (a chuva em mm/h; a chance em %, com `maximo` 100), cada uma na cor da escala do mapa. Abaixo de `minimo`, nada. */
export function Barras(props: {
  serie: ArrayLike<number>;
  cor: (v: number) => string;
  instantes: readonly number[];
  iAtual: number;
  rotulo: string;
  altura: number;
  cursor: Cursor;
  maximo?: number;
  minimo?: number;
}) {
  const { serie } = props;
  const maior = props.maximo ?? Math.max(1, ...Array.from(serie).filter(Number.isFinite));
  const minimo = props.minimo ?? 0.1;
  const A = GRAFICO_A;
  return (
    <GraficoTempo
      instantes={props.instantes}
      iAtual={props.iAtual}
      rotulo={props.rotulo}
      valor={() => null}
      resumo={null}
      soDesenho
      altura={props.altura}
      sob={props.cursor.sob}
      aoSob={props.cursor.aoSob}
      desenho={({ w, destaque }) => (
        <>
          <line x1="0" x2={props.instantes.length * w} y1={A - 0.4} y2={A - 0.4} stroke="rgba(169,182,204,0.35)" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
          {Array.from(serie).map((v, i) => {
            if (!Number.isFinite(v) || v < minimo) return null;
            const h = Math.max(1.2, (v / maior) * (A - 2));
            return <rect key={i} x={i * w + 0.2} y={A - h} width={Math.max(0.6, w - 0.4)} height={h} fill={props.cor(v)} opacity={i === destaque ? 1 : 0.85} />;
          })}
        </>
      )}
    />
  );
}

/**
 * Uma faixa: título, escala e o valor da hora do cursor à direita. A leitura
 * tem largura FIXA (a da mais longa, "18 km/h · raj. 36"), alinhada à direita:
 * passar o dedo ou o mouse muda os números, nunca a posição do resto.
 */
export function Faixa(props: { titulo: ReactNode; escala?: string | null; leitura: ReactNode; children: ReactNode }) {
  return (
    <div className="pt-1">
      <div className="flex items-baseline gap-1.5 text-[11px] leading-tight text-ink-300">
        <span className="min-w-0 shrink truncate font-medium text-ink-200">{props.titulo}</span>
        {props.escala && <span className="min-w-0 flex-1 truncate tabular-nums">{props.escala}</span>}
        <span className="ml-auto w-[18ch] shrink-0 truncate text-right text-xs font-semibold whitespace-nowrap text-ink-100 tabular-nums">{props.leitura}</span>
      </div>
      {props.children}
    </div>
  );
}

/** Eixo dos dias do lugar: o nome do dia no começo de cada um e, numa janela de 1 dia, as horas. */
export function EixoDias(props: { instantes: readonly number[]; fuso: number; hoje: number }) {
  const { instantes, fuso } = props;
  const n = instantes.length;
  const umDia = n <= 9;
  const marcas: { i: number; texto: string }[] = [];
  for (let i = 0; i < n; i++) {
    const t = instantes[i]!;
    const h = horaLocal(t, fuso);
    if (umDia) {
      if (i % 2 === 0) marcas.push({ i, texto: `${String(h).padStart(2, '0')}h` });
    } else if (i === 0 || inicioDoDiaLocal(t, fuso) !== inicioDoDiaLocal(instantes[i - 1]!, fuso)) {
      // Um dia com menos de 3 fatias na janela fica sem nome (não cabe e se espreme no vizinho).
      const dia = inicioDoDiaLocal(t, fuso);
      let fatias = 0;
      for (let j = i; j < n && inicioDoDiaLocal(instantes[j]!, fuso) === dia; j++) fatias++;
      if (fatias >= 3) marcas.push({ i, texto: dia === props.hoje ? 'hoje' : rotuloDia(dia, fuso) });
    }
  }
  return (
    <div className="relative mt-1 h-4 text-[11px] text-ink-300 pointer-coarse:text-xs" aria-hidden>
      {marcas.map(({ i, texto }) => (
        <span key={i} className="absolute -translate-x-0 tabular-nums whitespace-nowrap" style={{ left: `${(i / Math.max(1, n)) * 100}%` }}>
          {texto}
        </span>
      ))}
    </div>
  );
}

/** Setinhas de para onde o vento vai, a cada 6 h, numa linha sob a faixa do vento (Windy, meteoblue). */
export function SetasVento({ rumo }: { rumo: ArrayLike<number> }) {
  const n = rumo.length;
  return (
    <div aria-hidden className="pointer-events-none relative h-3">
      {Array.from(rumo).map((g, i) =>
        Number.isFinite(g) && i % 2 === 0 ? (
          <span key={i} className="absolute top-0 -translate-x-1/2 text-[10px] leading-3 text-[#d4ec7a]" style={{ left: `${centroPct(i, n)}%`, transform: `translateX(-50%) rotate(${g}deg)` }}>
            ↑
          </span>
        ) : null,
      )}
    </div>
  );
}
